import { z } from 'zod';
/**
 * Wire types for the Node backend ↔ Python Kennisagent service.
 *
 * This file is the SOURCE OF TRUTH. Python Pydantic models are generated from
 * this via JSON Schema + datamodel-code-generator (see agents/kennisagent/Makefile).
 * To change the wire shape: edit this file, then run `make schemas` in
 * agents/kennisagent/, commit both files. CI fails if `git diff` is non-empty
 * after regen.
 *
 * Slice 3a wire shape: POST /v1/agent/ask
 */
export const AgentTenantId = z.string().min(1).max(100);
export const AgentUserId = z.string().min(1).max(100);
/**
 * Four-mode answer-depth selector. See AgentAskRequest.mode for behaviour.
 */
export const AgentAskMode = z.enum([
    'quick',
    'deep_developer',
    'deep_marketing',
    'deep_customer_success',
    'deep_quality_analyst',
    'deep_legal',
    'deep_business_analyst',
]);
/**
 * Retrieval-breadth selector — independent of `mode`. Controls top-K of
 * the chunk retrieval (NOT the display chunk count). Default `balanced`
 * preserves current behaviour byte-for-byte. Wire-optional so older
 * callers omitting the field keep working unchanged.
 *   concise  → top-K 5  (faster, fewer sources)
 *   balanced → current default top-K (≈10 surfaced; depends on agent config)
 *   thorough → top-K 20 (wider net, more sources)
 */
export const CitationDepth = z.enum(['concise', 'balanced', 'thorough']);
/**
 * kb_admin-only answer-model override. Lets admins compare answer quality
 * across providers (Gemini Pro/Flash and Claude Opus/Sonnet on Vertex)
 * without redeploying. Non-admin callers that send a `modelId` are silently
 * forced to the default at the route layer — no 403, no UI footprint for
 * non-admin users. Absent on every wire payload from a non-admin client.
 */
export const AgentModelId = z.enum([
    'gemini-2.5-pro',
    'gemini-3.5-flash',
    'claude-opus-4-7',
    'claude-sonnet-latest',
]);
export const AgentAskRequest = z.object({
    /**
     * Caller-provided idempotency / tracing handle. Backend mints a UUID per
     * `/api/v1/agent/ask` call and forwards it.
     */
    requestId: z.string().min(1).max(100),
    tenantId: AgentTenantId,
    /**
     * For BigQuery telemetry actor_user_id field. NEVER used to filter retrieval —
     * Ring 1 corpus is shared. Slice 4 will add tenant-private Ring 2 lookups.
     */
    userId: AgentUserId.optional(),
    actorRole: z.string().max(50).optional(),
    /**
     * The asking user's Entra object id (2026-06-08 agent-tools design).
     * The agent passes it back verbatim as `askerEntraOid` on every
     * /api/v1/agent/tools/* RPC, so tool calls run under the asker's own
     * per-user OAuth in the AIAC gateway (the user only sees Jira tickets
     * they could already see). Absent for dev-token sessions — tool calls
     * then degrade with a "no Jira connection" message.
     */
    askerEntraOid: z.string().min(1).max(200).optional(),
    /**
     * My Agent personal mode (2026-06-09 design): the agent switches to the
     * personal tool registry (live per-user M365 reads) and appends the
     * personal prompt overlay. Org retrieval is unchanged. Only the
     * /api/v1/my-agent route sets this.
     */
    personal: z.boolean().optional(),
    /**
     * The English knowledge question. Capped at 8000 chars (≈1500 words)
     * to bound prompt cost while leaving room for paragraph-level
     * technical questions pasted from elsewhere. For longer source
     * material use the `attachment` field below (50K-char ceiling).
     */
    query: z.string().min(1).max(8000),
    /**
     * Optional explicit corpus scope from the frontend's scope picker. When
     * present, retrieval hard-filters to chunks whose `documentTitle` is in
     * this list. Capped at 30 (Firestore `in`-query limit). Absent or empty
     * means "scan the entire Ring 1 corpus" (default).
     */
    documentTitles: z.array(z.string().min(1).max(500)).max(30).optional(),
    /**
     * Last N user/assistant messages in the same chat (oldest-first), capped
     * to 10 (= 5 Q&A pairs) to bound prompt cost. Lets the answer-side agent
     * naturally reference earlier context ("aanvullend op de eerder besproken
     * SSRI-aanpak…") without re-citing prior sources unnecessarily.
     */
    priorMessages: z
        .array(z.object({
        role: z.enum(['user', 'assistant']),
        // Deep-mode answers can hit 12-20K chars (heavy tables + code).
        // Keep this in lock-step with the Python PriorMessageBody.content.
        content: z.string().min(1).max(32000),
    }))
        .max(10)
        .optional(),
    /**
     * Sticky head — the first user question in the chat, truncated. Always
     * rides along even after the original message itself drops out of the
     * `priorMessages` sliding window. Lets the agent stay grounded in the
     * conversation's original framing ("we started by asking about
     * depression treatment in adults") even at turn 12. Backend only sends
     * this when the thread is long enough that turn 1 has fallen out of
     * the sliding window — otherwise the first message is already in
     * `priorMessages` and we'd be duplicating tokens.
     */
    firstQuestionSummary: z.string().min(1).max(300).optional(),
    /**
     * 1-line summaries of what was cited in each prior assistant turn, deduped
     * across the thread, capped at 8 entries. Tells the agent what's already
     * been grounded so it doesn't waste a turn re-citing the same chunks.
     * Format: `"{documentTitle} ({source})"` where source is "SUPPOHRT" for
     * Ring 1 or the Ring 2 source label.
     */
    priorCitationSummaries: z.array(z.string().min(1).max(500)).max(8).optional(),
    /**
     * Ring 0 access flag. Backend sets this to `true` ONLY when the caller's
     * Entra group claims include SUPPOHRT_USERS or kb_adminS — that's
     * the load-bearing access control for internal-only docs (personnel
     * handbook, timechimp guides). Agent stays role-blind: it just sees the
     * flag and queries the kennisbank_ring0 collection if true. Forgotten
     * flag = no Ring 0 results (fail-safe), not a leak.
     */
    ring0Enabled: z.boolean().optional(),
    /**
     * ISO 27001 information-classification access groups (app-internal names,
     * mapped from Entra at login). Forwarded by the backend from
     * `req.auth.accessGroups`. The agent uses this to drop chunks classified
     * `confidential` / `restricted` that the caller has no access to BEFORE
     * returning to the backend. Defaults to `[]` so older callers that
     * haven't been updated still degrade safely (no confidential retrieval).
     */
    accessGroups: z.array(z.string().min(1).max(120)).max(20).optional(),
    /**
     * Four-mode answer depth. Quick (default / absent) = current behaviour
     * (~3s, Flash-routable, tight context). Deep modes pin Pro, widen
     * retrieval, raise the thinking budget, and use persona-specific system
     * prompts (developer / marketing / customer_success). Client preference,
     * not an RBAC gate.
     */
    mode: AgentAskMode.optional(),
    /**
     * Retrieval-breadth knob — see `CitationDepth`. Absent = balanced
     * (current default top-K). Only affects retrieval; display behaviour
     * (chunk numbering, source list rendering) is unchanged.
     */
    citationDepth: CitationDepth.optional(),
    /**
     * kb_admin-only model override. The route layer drops this for non-admin
     * callers (silently forces the agent default). When absent the agent uses
     * its existing tier-based routing (Gemini 2.5 Pro / Flash). When set the
     * answer step is pinned to the chosen model id; routing diagnostics
     * surface which model actually answered.
     */
    modelId: AgentModelId.optional(),
    /**
     * Canonical-facts overlay. Backend-set list of admin-curated authoritative
     * statements that override stale information in the retrieved fragments
     * (e.g. "The current OneView URL is oneview.nebula.co.za — older docs
     * reference firm-it.co.za, which is no longer valid."). Caller-supplied
     * value is ignored; the route layer always replaces with the current
     * enabled set from `canonical_facts/`. Capped at 50 facts × 1000 chars
     * each so the overlay stays within Pro's context budget.
     */
    canonicalFacts: z.array(z.string().min(1).max(1000)).max(50).optional(),
    /**
     * Optional user-attached file, always as text. Single attachment per
     * question. Text/code files (≤1 MB raw) are posted as-is; PDF / Word /
     * Excel (≤5 MB raw) are converted to text in the browser first. Either
     * way the text must fit in 1 MB (frontend lib/chat-attachment-limits).
     * The Python agent injects it into the user message as authoritative
     * context alongside retrieved knowledgebase fragments. Images are
     * rejected at the browser.
     */
    attachment: z
        .object({
        filename: z.string().min(1).max(200),
        mimeType: z.string().min(1).max(100),
        contentText: z.string().min(1).max(1_000_000),
        // 5 MB ceiling — covers the frontend's 5 MB raw-file cap for
        // binary docs (XLSX/PDF/DOCX). Extracted-text cap is the real
        // context-budget guard (1 MB max contentText above).
        sizeBytes: z.number().int().nonnegative().max(5_000_000),
    })
        .optional(),
    /**
     * Training-video scope from the "Ask the trainer" panel.
     *   - `this` + videoScopeId → constrain retrieval to a single video.
     *   - `videos` → constrain to all training-video chunks (sourceType='video').
     *   - `all` (or absent) → full knowledge base, unchanged behaviour.
     * Applied as a post-filter inside `retrieve.py` so no new composite
     * Firestore index is required.
     */
    videoScope: z.enum(['this', 'videos', 'all']).optional(),
    videoScopeId: z.string().min(1).max(120).optional(),
    /**
     * Canvas mode (issue #3). When present, the agent runs a canvas turn
     * instead of the regular ask pipeline. `query` carries the user's
     * per-turn instruction. Shape mirrors the Python `CanvasContextBody`
     * in agents/knowledge-agent/src/main.py.
     */
    canvas: z
        .object({
        mode: z.enum(['create', 'update']),
        currentMarkdown: z.string().max(200_000).nullable(),
        version: z.number().int().nonnegative().nullable(),
        starterTemplate: z
            .enum(['spec', 'client_presentation', 'test_cases', 'marketing_onepager', 'blank'])
            .nullable(),
        // Opaque pass-through — the agent only inspects chunkId / ring on
        // entries. Cap matches the Python validator.
        citationSnapshot: z.array(z.record(z.unknown())).max(500),
        maxChars: z.number().int().min(1000).max(200_000),
    })
        .optional(),
});
/**
 * One row in the scope-picker document list. The frontend renders these
 * as multi-select options. `sourceFile` is the bucket key + canonical
 * identifier; `documentTitle` is what the operator sees.
 */
export const AgentDocumentSummary = z.object({
    sourceFile: z.string().min(1).max(500),
    documentTitle: z.string().min(1).max(500),
    author: z.string().min(1).max(200),
    /** Trust tier — present once the scope endpoint reads the registry. */
    ring: z.enum(['ring0', 'ring1']).optional(),
    /** Confluence link-back, when the registry row was ingested via Confluence. */
    confluenceSourceId: z.string().min(1).max(200).optional(),
});
export const AgentDocumentsResponse = z.object({
    documents: z.array(AgentDocumentSummary),
});
/**
 * One source card rendered under the answer in the chat UI.
 */
export const AgentCitation = z.object({
    /**
     * Stable chunk ID (sha256 of sourceFile + chunkIndex + chunkText). Frontend
     * never displays this; it's used for click-through (slice 3b) and dedup.
     */
    chunkId: z.string().min(1).max(200),
    /**
     * Display title — manifest-supplied document title; falls back to PDF
     * basename minus extension when the manifest entry is missing.
     */
    title: z.string().min(1).max(500),
    /**
     * Original PDF basename (with extension). Used by the source-download
     * endpoint and for cross-referencing chunks back to the file in GCS.
     */
    sourceFile: z.string().min(1).max(500).optional(),
    /**
     * Author or institution responsible for the document (manifest-supplied).
     * Empty when unknown — the card hides the field rather than showing 'Onbekend'.
     */
    author: z.string().max(200).optional(),
    /**
     * The document's own date (authored/published) as RFC3339, when the
     * ingest could resolve it (file metadata or a date in the filename).
     * Drives the "as of <date>" line on the citation card + recency-aware
     * answering. Absent for legacy chunks / undated sources.
     */
    documentDate: z.string().datetime().optional(),
    /**
     * 1-based page number for the chunk's first character. Absent on
     * tool-result citations (live system rows have no pages).
     */
    page: z.number().int().min(1).optional(),
    /**
     * ≤300-char excerpt rendered on the card. Source-language (Dutch).
     */
    snippet: z.string().min(1).max(500),
    /**
     * Trust tier — drives the chip colour on the citation card.
     *   'ring0' = SUPPOHRT-internal corpus (personeelshandboek, way-we-work,
     *             timechimp guides). Visible only to internal SUPPOHRT staff
     *             (Entra group SUPPOHRT_USERS or kb_adminS); never to
     *             external practitioners. Distinct chip ("SUPPOHRT Intern").
     *   'ring1' = SUPPOHRT-curated clinical corpus (Mosterdgeel chip).
     *   'ring2' = verified external sources crawled weekly (Petrol chip).
     *   'tool'  = live agent-tool result (2026-06-08 agent-tools design) —
     *             synthetic citation with id `tool:<name>:<key>`; neutral
     *             "Live · Jira" chip, no corpus chunk behind it.
     * Defaults to 'ring1' when missing so legacy citations on existing
     * chats don't break.
     */
    ring: z.enum(['ring0', 'ring1', 'ring2', 'tool']).default('ring1'),
    /**
     * For ring='ring2', the canonical source identifier matching
     * shared/src/ring2-sources.ts. Drives the chip label ("NHG", "Trimbos"
     * etc.) and the "Open op {source}.nl" link target. Absent for ring1.
     */
    ring2Source: z.string().min(1).max(40).optional(),
    /**
     * For ring='ring2', the original URL of the article on the external
     * source. The "Open op {source}.nl" button opens this in a new tab.
     * Ring 1 chunks already have sourceFile pointing to the GCS object;
     * Ring 2 has no GCS file by design (link-out, never host).
     */
    sourceUrl: z.string().url().max(2000).optional(),
    /**
     * ISO 27001 — chunk-level classification surfaced on the citation card.
     * Default 'internal' for legacy chunks. Frontend renders a chip next to
     * the trust-tier chip when this is `confidential` or `restricted`.
     */
    classification: z.enum(['public', 'internal', 'confidential', 'restricted']).optional(),
    /**
     * Training-video metadata. Emitted by the Python agent when the cited
     * chunk came from the video ingest pipeline (sourceType='video'). The
     * frontend renders the citation as a clickable timestamp that jumps
     * the player to `startSeconds` (or, on the Knowledge Agent page,
     * deep-links to `/videos/{videoId}?t={startSeconds}`).
     */
    sourceType: z.enum(['document', 'video', 'code', 'tool']).optional(),
    videoId: z.string().min(1).max(120).optional(),
    startSeconds: z.number().nonnegative().optional(),
    endSeconds: z.number().nonnegative().optional(),
});
export const AgentAskOutcome = z.enum([
    'success',
    'low_confidence_rewrite',
    'empty_retrieval',
    'refused',
]);
export const AgentAskResponse = z.object({
    requestId: z.string().min(1).max(100),
    /**
     * Dutch answer text. May be a refusal string when outcome != 'success'.
     * Frontend renders verbatim — no Markdown processing yet (slice 3a).
     */
    answer: z.string().min(1).max(8000),
    // 30, not 10: an aggregation answer cites one synthetic tool id per
    // bucket (up to 20 by default) on top of any corpus chunks.
    citations: z.array(AgentCitation).max(30),
    outcome: AgentAskOutcome,
    /**
     * Multi-turn debug surface. Present when the agent reformulated the
     * (often terse) follow-up into a standalone Dutch query for retrieval.
     * Backend persists this on the assistant `ChatMessage` so a future debug
     * affordance can show "your question was interpreted as: …". Absent on
     * cold-start asks where no reformulation happened.
     */
    reformulatedQuery: z.string().min(1).max(1000).optional(),
    /**
     * Diagnostics for backend audit/telemetry (NOT shown to user). Token counts
     * come from Vertex SDK's GenerateContentResponse.usage_metadata.
     */
    diagnostics: z.object({
        promptTokens: z.number().int().nonnegative().optional(),
        completionTokens: z.number().int().nonnegative().optional(),
        durationMs: z.number().int().nonnegative(),
        citationsDropped: z.number().int().nonnegative().default(0),
        retrievedChunkCount: z.number().int().nonnegative(),
        modelId: z.string().min(1).max(100),
    }),
});
/**
 * Error envelope. Python service returns these with appropriate HTTP status.
 * Backend maps to user-facing Dutch copy per the plan's error UX section.
 */
export const AgentErrorCode = z.enum([
    'invalid_request',
    'unauthorized',
    'rate_limited',
    'timeout',
    'upstream_error',
    'internal_error',
]);
export const AgentErrorResponse = z.object({
    requestId: z.string().min(1).max(100).optional(),
    error: AgentErrorCode,
    message: z.string().min(1).max(1000),
});
//# sourceMappingURL=agent.js.map